import { useCallback } from "react";

import type { ReservationCancelFeeChargeInput } from "@/lib/gql/__generated__/graphql";

import { useCustomMutation } from "@/hooks/use-graphql-client";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import { RESERVATION_DETAIL_CANCEL_FEE_CHARGE_FOR_MANAGEMENT_RESERVATION_ITEM_DETAIL_MUTATION } from "./schema";
import * as Shared from "../../../../_shared";

export const useReservationDetailCancelFeeChargeMutation = () => {
	const { mutationFunc } = useCustomMutation(
		RESERVATION_DETAIL_CANCEL_FEE_CHARGE_FOR_MANAGEMENT_RESERVATION_ITEM_DETAIL_MUTATION,
	);
	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();

	const onChargeCancelFee = useCallback(
		async (input: ReservationCancelFeeChargeInput) => {
			showLoadingScreenCallback();
			return mutationFunc({
				variables: { input },
				update: (cache, { data }) => {
					/**
					 * キャッシュから予約詳細を取得
					 */
					const cached = cache.readQuery({
						query: Shared.Model.Repository.Query.RESERVATION_DETAIL_FOR_MANAGEMENT_RESERVATION_ITEM_QUERY,
						variables: { hotelID: input.hotelID, reservationId: input.reservationID },
					});
					/**
					 * レスポンスから更新された予約詳細を取得
					 */
					const updated = data?.reservationDetailCancelFeeCharge.reservationDetail;
					if (!cached || !updated) return;

					/**
					 * 予約更新のキャッシュを更新
					 */
					cache.writeQuery({
						query: Shared.Model.Repository.Query.RESERVATION_DETAIL_FOR_MANAGEMENT_RESERVATION_ITEM_QUERY,
						variables: { hotelID: input.hotelID, reservationId: input.reservationID },
						data: {
							reservationDetail: {
								...cached.reservationDetail,
								...updated,
							},
							repeaterReservationList: cached.repeaterReservationList,
						},
					});
				},
			})
				.then(({ errors }) => {
					if (!errors) {
						notifyUserEventInfo({
							variant: "default",
							title: "キャンセル料を請求しました",
						});
					}
				})
				.finally(() => {
					hideLoadingScreenCallback();
				});
		},
		[hideLoadingScreenCallback, mutationFunc, showLoadingScreenCallback],
	);

	return {
		onChargeCancelFee,
	};
};
